/**
 * JIRA Tickets Section
 *
 * Shows the Zafran remediation tickets pulled daily from JIRA for an
 * application/team. Data comes from S3 via the backend JIRA S3 API:
 *   GET /api/jira/application/{appId}/tickets
 *
 * Filters applied by the collector: status=New, summary starts with
 * "Remediate", reporter=SVC_ZAFRAN_JIRA_SYNC, top 100 by priority.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import Pagination from './common/Pagination';
import usePagination from './common/usePagination';
import BatchRemediationSection from './BatchRemediationSection';
import './JiraTicketsSection.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// AI provider used for the fix + PR. 'copilot_agent' delegates to the GitHub
// Copilot CODING AGENT (part of the org's Copilot Business subscription), which
// explores the repo and handles all change types - dependency bumps, IaC, and
// source code. (The GitHub Models inference API was retired on 2026-07-30, so
// the 'copilot' Models flow is no longer available.) The backend still applies
// deterministic pre-gates before delegating, so no-change findings (already
// patched, transitive/absent packages, asset/OS/host) are posted to JIRA
// instead of producing empty pull requests.
const DEFAULT_PROVIDER = 'copilot_agent';
const PROVIDER_LABEL = 'GitHub Copilot (coding agent)';

const PRIORITY_STYLE = {
  Highest: { color: '#dc2626', icon: '🔴' },
  High: { color: '#ea580c', icon: '🟠' },
  Medium: { color: '#ca8a04', icon: '🟡' },
  Low: { color: '#16a34a', icon: '🟢' },
  Lowest: { color: '#6b7280', icon: '⚪' },
};

// ---------------------------------------------------------------------------
// JIRA key normalization / validation (client-side, mirrors backend logic)
// ---------------------------------------------------------------------------
// Matches a standard JIRA issue key, e.g. MK-11411, CHSS-41523.
const JIRA_KEY_RE = /[A-Z][A-Z0-9]+-\d+/;

/**
 * Extract a bare JIRA issue key from user input. Accepts a bare key
 * (MK-11411) or a full browse URL (https://kone.atlassian.net/browse/MK-11411).
 * Returns the bare key (uppercase), or null when no valid key is present.
 */
const normalizeEpicInput = (raw) => {
  const trimmed = (raw || '').trim().toUpperCase();
  if (!trimmed) return null;
  const m = trimmed.match(JIRA_KEY_RE);
  return m ? m[0] : null;
};

// Render a coloured badge for a fixed ticket's current JIRA status. Terminal
// states (closed/rejected) get their own styling; anything else is "in progress".
const fixedStatusBadge = (status) => {
  if (!status) return <span className="jira-fixed-status unknown">Unknown</span>;
  const s = String(status).toLowerCase();
  let cls = 'in-progress';
  if (['closed', 'done', 'resolved'].includes(s)) cls = 'closed';
  else if (['rejected', 'cancelled', 'canceled'].includes(s)) cls = 'rejected';
  return <span className={`jira-fixed-status ${cls}`}>{status}</span>;
};

// A remediation link is only ever surfaced as a PULL REQUEST. The Copilot
// coding agent opens its PR asynchronously, so until that PR exists the stored
// link points at the tracking ISSUE - we never show that issue link. Instead we
// show a non-clickable "PR pending" state; once the real PR is resolved onto the
// record (read-time / daily), the clickable PR link is shown.
const isPullRequestUrl = (url) => typeof url === 'string' && /\/pull\/\d+/.test(url);
const remediationLinkOrPending = (url, number) => {
  if (isPullRequestUrl(url)) {
    return (
      <a className="jira-fix-prlink" href={url} target="_blank" rel="noopener noreferrer">
        ✅ View PR{number ? ` #${number}` : ''}
      </a>
    );
  }
  return (
    <span
      className="jira-pr-pending"
      title="Copilot is preparing the fix. The pull request link will appear here once it is opened."
    >
      ⏳ PR pending
    </span>
  );
};

// One affected repository's outcome for a MULTI-repo finding. A single Zafran
// finding can affect several repos of a multi-repo application (e.g. IES): some
// need a code change (Copilot opens a PR), others do not (no change). We show
// each repo's own state rather than a single arbitrary one.
const renderRepoResult = (r) => {
  if (isPullRequestUrl(r.prUrl)) {
    return (
      <a className="jira-fix-prlink" href={r.prUrl} target="_blank" rel="noopener noreferrer">
        ✅ {r.repoShort}: PR{r.prNumber ? ` #${r.prNumber}` : ''}
      </a>
    );
  }
  if (r.status === 'skipped') {
    return (
      <span className="jira-fix-skipped" title={r.message || 'No code change required'}>
        ℹ️ {r.repoShort}: No change needed
      </span>
    );
  }
  if (r.status === 'pr_closed') {
    return (
      <span className="jira-fix-error" title={r.message || 'The pull request was closed without merging'}>
        🚫 {r.repoShort}: PR closed (re-run)
      </span>
    );
  }
  if (['failed', 'error'].includes(r.status)) {
    return (
      <span className="jira-fix-error" title={r.message || 'Remediation failed'}>
        ❌ {r.repoShort}: Failed
      </span>
    );
  }
  return (
    <span className="jira-pr-pending" title="Copilot is preparing the fix for this repository.">
      ⏳ {r.repoShort}: PR pending
    </span>
  );
};

const renderRepoResults = (results) => (
  <div className="jira-repo-results">
    {(results || []).map((r) => (
      <div key={r.repository || r.repoShort} className="jira-repo-result">
        {renderRepoResult(r)}
      </div>
    ))}
  </div>
);

const isMultiRepo = (t) => !!(t && t.multiRepo && Array.isArray(t.repoResults) && t.repoResults.length);

// Bedrock's bucket for a fixed ticket - the SAME category the Batch
// Remediation tab grouped it under, reusing that tab's labels and colours so
// the two views read as one classification rather than two.
const BUCKET_LABEL = {
  HIGH: { label: 'High', icon: '🔴', cls: 'high' },
  MEDIUM: { label: 'Medium', icon: '🟡', cls: 'medium' },
  LOW: { label: 'Low', icon: '🟢', cls: 'low' },
};

// Categories offered by the Fixed tab's filter, plus the two honest absences.
const FIXED_BUCKET_FILTERS = ['HIGH', 'MEDIUM', 'LOW', 'UNCATEGORIZED'];

const bucketOf = (t) => String((t && t.bucket) || '').trim().toUpperCase();

const bucketBadge = (t) => {
  const bucket = bucketOf(t);
  const style = BUCKET_LABEL[bucket];
  if (!style) {
    // Not an assessment of "low" - Bedrock never assessed this ticket (fixed
    // interactively, or fixed before categorization ran). Saying so is more
    // use than showing a category it does not have.
    return (
      <span className="jira-bucket none" title="Bedrock has no severity assessment for this ticket">
        Not assessed
      </span>
    );
  }
  const evidence = [
    t.aiChangeType,
    t.aiChangeScope ? `${t.aiChangeScope} scope` : '',
    t.aiConfidence !== null && t.aiConfidence !== undefined && t.aiConfidence !== ''
      ? `confidence ${t.aiConfidence}`
      : '',
  ].filter(Boolean).join(' · ');
  return (
    <span
      className={`jira-bucket ${style.cls}`}
      title={[
        `Bedrock bucket: ${bucket}`,
        evidence,
        t.batchRunId ? 'Remediated by a batch run' : '',
      ].filter(Boolean).join(' — ')}
    >
      {style.icon} {style.label}
      {t.batchRunId ? <span className="jira-bucket-batch"> batch</span> : null}
    </span>
  );
};

/**
 * Manage repositories & config panel (user-managed JIRA remediation config).
 *
 * Mirrors the Code-Scan repo-management UX. Lets an operator set the JIRA EPIC
 * (parent key), add/remove target repositories, edit PR settings (base branch,
 * head-branch pattern, comment format), and store/rotate the GitHub token per
 * application — all from the UI, backed by /api/jira-config/*.
 *
 * This is entirely SELF-CONTAINED and additive: it holds its own state and does
 * not touch the read-only ticket list or the "Fix with iCOP" flow. With an
 * empty store the backend still resolves the current hardcoded config, so
 * hiding/using this panel never changes remediation behavior.
 */
const JiraConfigPanel = ({ appId, onEpicAdded }) => {
  const [open, setOpen] = useState(false);
  const [repos, setRepos] = useState([]);
  // Multiple EPICs (parent keys) per application. `newEpic` holds the add input.
  const [epics, setEpics] = useState([]);
  const [newEpic, setNewEpic] = useState('');
  const [epicSource, setEpicSource] = useState('none');
  const [hasTeamToken, setHasTeamToken] = useState(false);
  // User-level token: the signed-in user's OWN GitHub token for this app. When
  // set it overrides the team token for THEIR remediation runs.
  const [hasUserToken, setHasUserToken] = useState(false);
  const [userTokenValue, setUserTokenValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  // Add-repo form.
  const [repoUrl, setRepoUrl] = useState('');
  const [baseBranch, setBaseBranch] = useState('');
  const [headBranchPattern, setHeadBranchPattern] = useState('');
  const [prCommentFormat, setPrCommentFormat] = useState('');
  const [busy, setBusy] = useState(false);
  // Single TEAM-LEVEL GitHub token (one token for ALL of this application's
  // repositories). Held only while the operator is typing; never populated from
  // the server (the value is write-only / never returned).
  const [teamTokenValue, setTeamTokenValue] = useState('');

  const base = `${API_URL}/api/jira-config/${encodeURIComponent(appId)}`;

  const refresh = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      const [reposRes, epicsRes, teamRes, userRes] = await Promise.all([
        axios.get(`${base}/repositories`),
        axios.get(`${base}/epics`),
        axios.get(`${base}/team-token`),
        axios.get(`${base}/user-token`).catch(() => ({ data: {} })),
      ]);
      setRepos(Array.isArray(reposRes.data?.repositories) ? reposRes.data.repositories : []);
      setEpics(Array.isArray(epicsRes.data?.epics) ? epicsRes.data.epics : []);
      setEpicSource(epicsRes.data?.source || 'none');
      setHasTeamToken(!!teamRes.data?.hasTeamToken);
      setHasUserToken(!!userRes.data?.hasUserToken);
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to load config' });
    } finally {
      setLoading(false);
    }
  }, [appId, base]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const addEpic = async () => {
    const raw = newEpic.trim();
    if (!raw) {
      setNotice({ type: 'error', text: 'Enter an EPIC / parent key (e.g. MK-11411).' });
      return;
    }
    // Accept either a bare key (MK-11411) or a full browse URL
    // (https://kone.atlassian.net/browse/MK-11411). Anything else is rejected
    // here with an inline error so no invalid value reaches the backend.
    const key = normalizeEpicInput(raw);
    if (!key) {
      setNotice({
        type: 'error',
        text: 'Enter a valid JIRA EPIC key (e.g. MK-11411) or its link (e.g. https://kone.atlassian.net/browse/MK-11411).',
      });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.post(`${base}/epics`, { epic: key });
      setEpics(Array.isArray(res.data?.epics) ? res.data.epics : epics);
      setEpicSource('ui');
      setNewEpic('');
      // The backend kicks off a runtime JIRA pull for this app so the new
      // epic's tickets appear now instead of after the next daily collection.
      const pullTriggered = res.data?.pullTriggered;
      setNotice({
        type: 'ok',
        text: pullTriggered
          ? `EPIC ${key} added. Pulling its tickets now — this can take up to a minute.`
          : `EPIC ${key} added.`,
      });
      // Refresh the tickets list in the parent section. Give the async
      // collector a moment to write the fresh data to S3, then reload.
      if (typeof onEpicAdded === 'function') {
        setTimeout(() => { try { onEpicAdded(); } catch (_) { /* noop */ } }, 8000);
      }
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Could not add EPIC' });
    } finally {
      setBusy(false);
    }
  };

  const removeEpic = async (key) => {
    setBusy(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.delete(`${base}/epics/${encodeURIComponent(key)}`);
      setEpics(Array.isArray(res.data?.epics) ? res.data.epics : epics.filter((e) => e !== key));
      setNotice({ type: 'ok', text: `EPIC ${key} removed.` });
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Could not remove EPIC' });
    } finally {
      setBusy(false);
    }
  };

  const addRepo = async () => {
    if (!repoUrl.trim()) {
      setNotice({ type: 'error', text: 'Enter a GitHub repository link.' });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      await axios.post(`${base}/repository`, {
        repositoryUrl: repoUrl.trim(),
        baseBranch: baseBranch.trim(),
        headBranchPattern: headBranchPattern.trim(),
        prCommentFormat: prCommentFormat.trim(),
      });
      setNotice({ type: 'ok', text: 'Repository saved.' });
      setRepoUrl(''); setBaseBranch(''); setHeadBranchPattern('');
      setPrCommentFormat('');
      refresh();
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Could not save repository' });
    } finally {
      setBusy(false);
    }
  };

  const removeRepo = async (repoId, repoName) => {
    setBusy(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      await axios.delete(`${base}/repository/${encodeURIComponent(repoId)}`);
      setNotice({ type: 'ok', text: `${repoName} removed from the UI list.` });
      refresh();
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Could not remove repository' });
    } finally {
      setBusy(false);
    }
  };

  // Save the ONE team-level GitHub token used for every repository of this
  // application. The backend stores it as the team secret and marks all repo
  // rows team-scoped; we carry the write on the first configured repo (any repo
  // works — the token is team-level, not repo-specific).
  const saveTeamToken = async () => {
    if (!teamTokenValue.trim()) {
      setNotice({ type: 'error', text: 'Token cannot be empty.' });
      return;
    }
    const carrierRepoId = repos[0]?.repoId;
    if (!carrierRepoId) {
      setNotice({
        type: 'error',
        text: 'Add at least one repository first, then save the team token.',
      });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      await axios.put(`${base}/repository/${encodeURIComponent(carrierRepoId)}/token`, {
        token: teamTokenValue.trim(),
        saveAsTeamToken: true,
      });
      setNotice({ type: 'ok', text: `Team token saved — used for all ${appId} repositories.` });
      setTeamTokenValue('');
      refresh();
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Could not store the team token' });
    } finally {
      setBusy(false);
    }
  };

  const saveUserToken = async () => {
    if (!userTokenValue.trim()) {
      setNotice({ type: 'error', text: 'Token cannot be empty.' });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      await axios.put(`${base}/user-token`, { token: userTokenValue.trim() });
      setNotice({ type: 'ok', text: 'Your token was saved — used for your remediations instead of the team token.' });
      setUserTokenValue('');
      setHasUserToken(true);
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Could not store your token' });
    } finally {
      setBusy(false);
    }
  };

  const removeUserToken = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const axios = (await import('axios')).default;
      await axios.delete(`${base}/user-token`);
      setNotice({ type: 'ok', text: 'Your token was removed. The team token will be used.' });
      setHasUserToken(false);
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || err.message || 'Could not remove your token' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="jira-config-panel">
      <button className="jira-config-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} ⚙️ Manage repositories &amp; config
      </button>
      {open && (
        <div className="jira-config-body">
          <p className="jira-config-hint">
            Overrides the built-in config for <strong>{appId}</strong>. Anything left unset falls
            back to the current defaults, so nothing changes until you save here.
          </p>

          {notice && (
            <div className={`jira-config-notice ${notice.type}`}>{notice.text}</div>
          )}
          {loading && <div className="jira-loading"><div className="spinner-small" /><span>Loading…</span></div>}

          {/* EPIC */}
          <div className="jira-config-row">
            <label>JIRA EPICs (parent keys)</label>
            {/* Existing EPICs: each with a Remove button. Tickets are pulled
                from ALL of these; the UI below can filter by a single EPIC. */}
            {epics.length > 0 ? (
              <ul className="jira-epic-list">
                {epics.map((e) => (
                  <li key={e} className="jira-epic-item">
                    <code>{e}</code>
                    <button
                      type="button"
                      className="jira-epic-remove"
                      disabled={busy}
                      title={`Remove ${e}`}
                      onClick={() => removeEpic(e)}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="jira-epic-empty">No EPICs configured yet. Add one or more below.</p>
            )}
            {/* Add a new EPIC. */}
            <div className="jira-config-inline">
              <input
                type="text"
                value={newEpic}
                placeholder="e.g. CHSS-41523"
                onChange={(e) => setNewEpic(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEpic(); } }}
              />
              <button className="jira-config-btn" disabled={busy} onClick={addEpic}>Add EPIC</button>
              <span className={`jira-config-src ${epicSource}`}>source: {epicSource}</span>
            </div>
          </div>

          {/* Team-level GitHub token — ONE token for ALL of this application's
              repositories (not per-repo). */}
          <div className="jira-config-card jira-token-card">
            <div className="jira-config-card-head">
              <h4>GitHub token (team-level)</h4>
              <span className={`jira-token-status ${hasTeamToken ? 'set' : 'unset'}`}>
                {hasTeamToken ? '✓ Team token set' : '○ No team token'}
              </span>
            </div>
            <p className="jira-config-hint">
              One GitHub token is stored per team and used for <strong>all {appId} repositories</strong>.
              Stored securely and never shown. Use a token with repo access
              (Contents, Pull requests, Issues), authorized for SSO if your org requires it.
            </p>
            <div className="jira-config-inline">
              <input
                type="password"
                value={teamTokenValue}
                placeholder={hasTeamToken ? 'Paste a new token to rotate' : 'Paste GitHub token'}
                autoComplete="new-password"
                onChange={(e) => setTeamTokenValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveTeamToken(); }}
              />
              <button className="jira-config-btn primary" disabled={busy} onClick={saveTeamToken}>
                {busy ? 'Saving…' : (hasTeamToken ? 'Rotate token' : 'Save token')}
              </button>
            </div>
          </div>

          {/* User-level GitHub token — the signed-in user's OWN token. When set
              it OVERRIDES the team token for THAT user's remediation runs, so
              the Copilot coding agent acts as them. Optional. */}
          <div className="jira-config-card jira-token-card">
            <div className="jira-config-card-head">
              <h4>My GitHub token (optional)</h4>
              <span className={`jira-token-status ${hasUserToken ? 'set' : 'unset'}`}>
                {hasUserToken ? '✓ Using my token' : '○ Using team token'}
              </span>
            </div>
            <p className="jira-config-hint">
              Optionally use <strong>your own</strong> GitHub token for the fixes you trigger.
              When set, iCOP uses it instead of the team token for <strong>your</strong> remediations
              (the PR / Copilot agent acts as you). If not set, the team token is used.
              Stored securely and never shown; needs repo access (Contents, Pull requests, Issues),
              SSO-authorized if required.
            </p>
            <div className="jira-config-inline">
              <input
                type="password"
                value={userTokenValue}
                placeholder={hasUserToken ? 'Paste a new token to rotate' : 'Paste your GitHub token'}
                autoComplete="new-password"
                onChange={(e) => setUserTokenValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveUserToken(); }}
              />
              <button className="jira-config-btn primary" disabled={busy} onClick={saveUserToken}>
                {busy ? 'Saving…' : (hasUserToken ? 'Rotate my token' : 'Save my token')}
              </button>
              {hasUserToken && (
                <button className="jira-config-btn" disabled={busy} onClick={removeUserToken}>
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* Repo list */}
          <div className="jira-config-repos">
            <h4>Repositories</h4>
            {repos.length === 0 ? (
              <p className="jira-config-empty">No repositories configured yet.</p>
            ) : (
              <table className="jira-config-table">
                <thead>
                  <tr><th>Repository</th><th>Base branch</th><th>Source</th><th className="jira-col-action">Action</th></tr>
                </thead>
                <tbody>
                  {repos.map((r) => (
                    <tr key={r.repository}>
                      <td className="jira-repo-name">{r.repository}</td>
                      <td>{r.baseBranch || '—'}</td>
                      <td><span className={`jira-config-src ${r.source}`}>{r.source}</span></td>
                      <td className="jira-col-action">
                        {r.source !== 'hardcoded' && (
                          <button
                            className="jira-config-link danger"
                            disabled={busy}
                            onClick={() => removeRepo(r.repoId, r.repository)}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Add repo */}
          <div className="jira-config-add">
            <h4>Add / update a repository</h4>
            <input
              type="text"
              value={repoUrl}
              placeholder="https://github.com/owner/repo"
              onChange={(e) => setRepoUrl(e.target.value)}
            />
            <div className="jira-config-grid">
              <input type="text" value={baseBranch} placeholder="Base branch (e.g. main)" onChange={(e) => setBaseBranch(e.target.value)} />
              <input type="text" value={headBranchPattern} placeholder="Head branch pattern (e.g. icop/jira-fix-{id})" onChange={(e) => setHeadBranchPattern(e.target.value)} />
            </div>
            <input
              type="text"
              value={prCommentFormat}
              placeholder="PR comment format (optional)"
              onChange={(e) => setPrCommentFormat(e.target.value)}
            />
            <button className="jira-config-btn primary" disabled={busy} onClick={addRepo}>
              {busy ? 'Saving…' : 'Save repository'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const JiraTicketsSection = ({ appId }) => {
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState('');
  // EPIC filter: '' = all EPICs; otherwise a parent key. All tickets carry
  // their own parentKey, so this filters the already-loaded list client-side.
  const [epicFilter, setEpicFilter] = useState('');
  // Free-text search over the pulled tickets. Matches the JIRA key (e.g.
  // "ABC-1234") or the summary, case-insensitive. All tickets are already in
  // state (the collector pulls every ticket), so this filters client-side.
  const [searchQuery, setSearchQuery] = useState('');
  // Separate search box for the Fixed tab (key / summary / team / fixed-by).
  const [fixedSearchQuery, setFixedSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const PAGE_SIZE = 10;
  // Remediation (PR creation) state, keyed by ticket key.
  const [remediating, setRemediating] = useState(null);
  const [remediationStatus, setRemediationStatus] = useState({});
  // Already-fixed tickets (remediated by iCOP). Persisted in DynamoDB for 90
  // days; the current JIRA status is refreshed once a day by the collector.
  const [fixedTickets, setFixedTickets] = useState([]);
  const [fixedLoading, setFixedLoading] = useState(true);
  // Fixed-tab category filter: '' = every category. Matches the Batch
  // Remediation buckets so a batch fix is findable under the same category it
  // was remediated as.
  const [fixedBucketFilter, setFixedBucketFilter] = useState('');
  // Fixed-tab rows the user is currently re-running. While a re-run is in
  // flight the backend record flips to a non-terminal (in-progress) state, so
  // list_fixed_tickets legitimately stops returning it (it now belongs in Open
  // until the run finishes). To avoid the row vanishing under the user mid
  // re-run, we remember its last-known Fixed row here and merge it back into the
  // Fixed list until the re-run reaches a terminal state. Keyed by ticket key.
  const rerunningFixedRef = useRef({});
  // Active tab within the single JIRA section: 'open' | 'fixed' | 'batch'.
  const [view, setView] = useState('open');
  // Ticket awaiting confirmation in the in-app "create fix" dialog.
  const [confirmTicket, setConfirmTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setError(null);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.get(`${API_URL}/api/jira/application/${encodeURIComponent(appId)}/tickets`);
      const data = res.data || {};
      const ticketList = Array.isArray(data.tickets) ? data.tickets : [];
      setTickets(ticketList);
      // Seed remediation state from the server so a ticket that already has a PR
      // shows the PR link (and hides the Fix button) across reloads / other users.
      const seed = {};
      ticketList.forEach((t) => {
        if (t && t.key && (t.prUrl || t.remediationStatus)) {
          seed[t.key] = {
            status: t.remediationStatus,
            message: t.remediationMessage || t.remediationStatus,
            prUrl: t.prUrl,
            prNumber: t.prNumber,
            // When the remediation was recorded - drives the 1-day Open-tab
            // grace window (see isAgedOut).
            updatedAt: t.remediationUpdatedAt,
            // Set when a batch run produced this remediation. Such a ticket
            // leaves the Open tab as soon as it is processed, with no grace
            // window (see isAgedOut).
            batchRunId: t.batchRunId || null,
          };
        }
      });
      setRemediationStatus((prev) => ({ ...prev, ...seed }));
      setSummary(data.summary || null);
      setTotalCount(
        data.summary && typeof data.summary.totalCount === 'number' ? data.summary.totalCount : null
      );
      setLastUpdated(data.lastUpdated || null);
      if (!data.tickets || data.tickets.length === 0) {
        setError(data.message || 'No JIRA tickets found. Data is collected daily.');
      }
    } catch (err) {
      console.error('Error fetching JIRA tickets:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch JIRA tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  // Fetch the tickets iCOP has already fixed (from the remediation store).
  const fetchFixed = useCallback(async () => {
    if (!appId) return;
    setFixedLoading(true);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.get(`${API_URL}/api/jira/application/${encodeURIComponent(appId)}/fixed`);
      const list = Array.isArray(res.data?.tickets) ? res.data.tickets : [];
      // Keep any row that's mid re-run visible even if the backend (correctly)
      // omits it while its record is temporarily in an in-progress state. When
      // the run reaches a terminal state we clear it from the ref and the fresh
      // backend row takes over.
      const preserved = rerunningFixedRef.current;
      const present = new Set(list.map((t) => t && t.key));
      const merged = [...list];
      Object.keys(preserved).forEach((key) => {
        if (!present.has(key) && preserved[key]) {
          merged.push(preserved[key]);
        }
      });
      setFixedTickets(merged);
    } catch (err) {
      console.error('Error fetching fixed tickets:', err);
      setFixedTickets([]);
    } finally {
      setFixedLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    fetchTickets();
    fetchFixed();
  }, [fetchTickets, fetchFixed]);

  // Poll remediation status until a PR is created (or it fails).
  const pollStatus = useCallback(async (ticketKey, remediationId) => {
    const axios = (await import('axios')).default;
    let attempts = 0;
    const maxAttempts = 30; // ~5 min at 10s
    const poll = async () => {
      if (attempts >= maxAttempts) {
        // Never leave the user hanging: report that we stopped watching. Keep
        // the preserved Fixed row (it was fixed before the re-run) so it does
        // not vanish; a later Refresh reflects the backend's real state.
        setRemediationStatus((prev) => ({
          ...prev,
          [ticketKey]: {
            ...(prev[ticketKey] || {}),
            status: 'error',
            message: 'Timed out waiting for the PR. It may still complete — refresh later or check logs.',
          },
        }));
        return;
      }
      attempts += 1;
      try {
        const res = await axios.get(`${API_URL}/api/remediation/${remediationId}/status`);
        const s = res.data || {};
        setRemediationStatus((prev) => ({
          ...prev,
          [ticketKey]: {
            status: s.status,
            message: s.message || s.statusLabel || s.status,
            prUrl: s.prUrl,
            prNumber: s.prNumber,
            error: s.error,
            // Freshly processed this session -> starts the 1-day Open-tab window.
            updatedAt: Date.now(),
          },
        }));
        if (['pr_created', 'pr_merged', 'verified'].includes(s.status)) {
          // A PR was created: this ticket is now "fixed", refresh that list.
          // Drop the preserved re-run row so the fresh backend row takes over.
          if (rerunningFixedRef.current[ticketKey]) {
            const next = { ...rerunningFixedRef.current };
            delete next[ticketKey];
            rerunningFixedRef.current = next;
          }
          fetchFixed();
          return; // terminal
        }
        if (['failed', 'skipped', 'error', 'pr_closed'].includes(s.status)) {
          // Terminal but not a fresh PR: keep the last-known Fixed row (its
          // prior PR/no-change result) rather than dropping it. Refresh so the
          // backend's current view (which may still include it) wins.
          if (rerunningFixedRef.current[ticketKey]) {
            const next = { ...rerunningFixedRef.current };
            delete next[ticketKey];
            rerunningFixedRef.current = next;
          }
          fetchFixed();
          return; // terminal
        }
      } catch (err) {
        // keep polling through transient errors
      }
      setTimeout(poll, 10000);
    };
    setTimeout(poll, 5000);
  }, [fetchFixed]);

  // Open the in-app confirmation dialog for a ticket (replaces window.confirm).
  const requestFix = useCallback((ticket) => {
    if (!ticket || remediating === ticket.key) return;
    setConfirmTicket(ticket);
  }, [remediating]);

  // Trigger the backend remediation agent to read the JIRA ticket and create a PR.
  const runFix = useCallback(async (ticket) => {
    if (!ticket || remediating === ticket.key) return;
    const provider = DEFAULT_PROVIDER;

    // If this ticket is being re-run from the Fixed tab, remember its current
    // Fixed row so fetchFixed keeps it visible while the record is temporarily
    // in an in-progress (non-terminal) state during the re-run.
    const existingFixedRow = fixedTickets.find((f) => f && f.key === ticket.key);
    if (existingFixedRow) {
      rerunningFixedRef.current = {
        ...rerunningFixedRef.current,
        [ticket.key]: existingFixedRow,
      };
    }

    setRemediating(ticket.key);
    setRemediationStatus((prev) => ({
      ...prev,
      [ticket.key]: { status: 'starting', message: 'Starting remediation...' },
    }));

    try {
      const axios = (await import('axios')).default;
      const res = await axios.post(
        `${API_URL}/api/vulnerabilities/${encodeURIComponent(ticket.key)}/remediate`,
        {
          source: 'jira',
          application: appId,
          team: appId,
          jiraTeam: ticket.team,
          summary: ticket.summary,
          provider,
        }
      );
      const result = res.data || {};
      setRemediationStatus((prev) => ({
        ...prev,
        [ticket.key]: {
          status: result.status || 'analyzing',
          message: result.message || 'Remediation triggered',
          remediationId: result.remediationId,
        },
      }));
      if (result.remediationId) {
        pollStatus(ticket.key, result.remediationId);
      }
    } catch (err) {
      setRemediationStatus((prev) => ({
        ...prev,
        [ticket.key]: {
          status: 'error',
          message: err.response?.data?.error || err.message || 'Failed to trigger remediation',
        },
      }));
    } finally {
      setRemediating(null);
    }
  }, [appId, remediating, pollStatus, fixedTickets]);

  const fixButtonLabel = (ticketKey) => {
    const st = remediationStatus[ticketKey]?.status;
    if (remediating === ticketKey || st === 'starting') return '⏳ Starting...';
    if (['analyzing', 'generating_fix', 'testing', 'pending'].includes(st)) return '⏳ Working...';
    if (['pr_created', 'pr_merged', 'verified'].includes(st)) return '✅ PR Created';
    if (st === 'skipped') return 'ℹ️ No change needed';
    if (st === 'pr_closed') return '🚫 PR closed - Re-run';
    if (['failed', 'error'].includes(st)) return '❌ Failed - Retry';
    return '🔧 Fix With iCOP';
  };

  // Label for the "run again" button in the Fixed tab. Re-run is ALWAYS
  // offered here (even for tickets that already produced a PR or a no-change
  // result); we only show a transient label while a run is in progress.
  const rerunButtonLabel = (ticketKey) => {
    const st = remediationStatus[ticketKey]?.status;
    if (remediating === ticketKey || st === 'starting') return '⏳ Starting...';
    if (['analyzing', 'generating_fix', 'testing', 'pending'].includes(st)) return '⏳ Working...';
    return '🔁 Re-run agent';
  };

  // Label for the action button on a MULTI-repo ticket in the Open tab. Shows a
  // transient label while a run is in flight; otherwise "Fix With iCOP" the
  // first time and "Re-run agent" once at least one attempt has been recorded
  // (e.g. a prior failure), so a finding that failed across its repos can be
  // retried in place.
  const multiRepoActionLabel = (ticketKey) => {
    const st = remediationStatus[ticketKey]?.status;
    if (remediating === ticketKey || st === 'starting') return '⏳ Starting...';
    if (['analyzing', 'generating_fix', 'testing', 'pending'].includes(st)) return '⏳ Working...';
    return st ? '🔁 Re-run agent' : '🔧 Fix With iCOP';
  };

  // A ticket "has a PR" once remediation reached a terminal PR state with a
  // link. In that case we hide the Fix button and show the PR link instead.
  const hasPr = (ticketKey) => {
    const st = remediationStatus[ticketKey];
    return !!(st && st.prUrl && isPullRequestUrl(st.prUrl) &&
      ['pr_created', 'pr_merged', 'verified'].includes(st.status));
  };

  // Remediation reached a "PR" state but the link is still the Copilot tracking
  // issue (the PR hasn't been opened/resolved yet). We show "PR pending", never
  // the issue link.
  const isPrPending = (ticketKey) => {
    const st = remediationStatus[ticketKey];
    return !!(st && st.prUrl && !isPullRequestUrl(st.prUrl) &&
      ['pr_created', 'pr_merged', 'verified'].includes(st.status));
  };

  // 'skipped' is terminal: no code change was required (e.g. the package isn't
  // in this repo), so there's nothing to fix - don't let the user re-run it.
  const isSkipped = (ticketKey) => remediationStatus[ticketKey]?.status === 'skipped';

  // A ticket is "processed" once the agent reached a terminal outcome: a PR was
  // created, or it was recorded as no-change ('skipped'). Processed tickets also
  // live in the Fixed tab, but we KEEP them in the Open tab (with their status)
  // for a grace window of one day after processing, then hide them here so the
  // Open tab returns to only actionable work. (They naturally drop out anyway
  // once the daily collector re-pulls JIRA and the ticket is no longer "New".)
  const PROCESSED_STATUSES = ['pr_created', 'pr_merged', 'verified', 'skipped'];
  const PROCESSED_GRACE_MS = 24 * 60 * 60 * 1000; // 1 day
  const isProcessed = (ticketKey) =>
    PROCESSED_STATUSES.includes(remediationStatus[ticketKey]?.status);

  // Milliseconds since the remediation was recorded (0 when we have no timestamp,
  // e.g. it was just processed this session -> treat as "now", keep it visible).
  const processedAgeMs = (ticketKey) => {
    const ts = remediationStatus[ticketKey]?.updatedAt;
    if (!ts) return 0;
    const ms = typeof ts === 'number' ? ts : Date.parse(ts);
    return Number.isNaN(ms) ? 0 : Date.now() - ms;
  };

  // A batch run remediated this ticket. The grace window exists so whoever
  // clicked "Fix With iCOP" sees the outcome where they clicked; a batch run is
  // tracked in the Batch Remediation tab instead, so holding its tickets in
  // Open only double-counts work that is already done.
  const isBatchRemediated = (ticketKey) =>
    !!remediationStatus[ticketKey]?.batchRunId;

  // Hide from the Open tab once processed - immediately for a batch-remediated
  // ticket, otherwise after the grace window.
  const isAgedOut = (ticketKey) => {
    if (!isProcessed(ticketKey)) return false;
    if (isBatchRemediated(ticketKey)) return true;
    return processedAgeMs(ticketKey) > PROCESSED_GRACE_MS;
  };

  // Tab label counts everything still shown in the Open tab (actionable +
  // recently-processed within the grace window).
  const openCount = tickets.filter((t) => !isAgedOut(t.key)).length;

  const search = searchQuery.trim().toLowerCase();
  const visible = (priorityFilter
    ? tickets.filter((t) => t.priority === priorityFilter)
    : tickets
  )
    .filter((t) => !isAgedOut(t.key))
    .filter((t) => (epicFilter ? t.parentKey === epicFilter : true))
    .filter((t) => {
      if (!search) return true;
      const key = String(t.key || '').toLowerCase();
      const summary = String(t.summary || '').toLowerCase();
      return key.includes(search) || summary.includes(search);
    });

  // Numbered pagination (10 per page) over the visible open tickets. Resets to
  // page 1 when the filter or the underlying data changes.
  const {
    page: openPage,
    setPage: setOpenPage,
    pageCount: openPageCount,
    pageItems: shown,
    totalItems: openTotal,
  } = usePagination(visible, PAGE_SIZE, [priorityFilter, epicFilter, searchQuery, tickets]);

  // ---- Fixed tab: client-side search + numbered pagination ----
  // Search matches JIRA key, summary, team, or who fixed it (case-insensitive).
  const fixedSearch = fixedSearchQuery.trim().toLowerCase();
  // Counts per Bedrock category across every fixed ticket, so the filter can
  // show its own size and an empty category is still selectable.
  const fixedBucketCounts = fixedTickets.reduce((acc, t) => {
    const bucket = BUCKET_LABEL[bucketOf(t)] ? bucketOf(t) : 'UNCATEGORIZED';
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});
  const fixedVisible = fixedTickets
    .filter((t) => {
      if (!fixedBucketFilter) return true;
      const bucket = BUCKET_LABEL[bucketOf(t)] ? bucketOf(t) : 'UNCATEGORIZED';
      return bucket === fixedBucketFilter;
    })
    .filter((t) => {
      if (!fixedSearch) return true;
      const key = String(t.key || '').toLowerCase();
      const summary = String(t.summary || '').toLowerCase();
      const team = String(t.team || '').toLowerCase();
      const fixedBy = String(t.fixedBy || '').toLowerCase();
      return key.includes(fixedSearch) || summary.includes(fixedSearch)
        || team.includes(fixedSearch) || fixedBy.includes(fixedSearch);
    });
  const {
    page: fixedPage,
    setPage: setFixedPage,
    pageCount: fixedPageCount,
    pageItems: fixedShown,
    totalItems: fixedTotal,
  } = usePagination(fixedVisible, PAGE_SIZE,
    [fixedSearchQuery, fixedBucketFilter, fixedTickets]);

  const priorityCounts = (summary && summary.byPriority) || {};
  // EPIC options for the filter dropdown: prefer the collector-provided list,
  // else derive the distinct parent keys present in the loaded tickets.
  const epicOptions = (() => {
    const fromSummary = Array.isArray(summary?.parents) ? summary.parents.filter(Boolean) : [];
    if (fromSummary.length) return fromSummary;
    const seen = [];
    for (const t of tickets) {
      if (t.parentKey && !seen.includes(t.parentKey)) seen.push(t.parentKey);
    }
    return seen;
  })();
  const epicCounts = (summary && summary.byEpic) || {};

  return (
    <>
    <section className="section-container jira-section">
      <h3 className="section-title">
        🎫 Zafran Remediation Tickets (JIRA)
        {totalCount !== null && (
          <span className="jira-badge" title="Total open remediation tickets in JIRA">
            {totalCount.toLocaleString()} total
          </span>
        )}
        {tickets.length > 0 && <span className="jira-badge">{tickets.length} shown</span>}
      </h3>

      {/* User-managed config (EPIC / repos / token / PR settings). Additive and
          collapsible; the read-only ticket flow below is unchanged. */}
      <JiraConfigPanel appId={appId} onEpicAdded={fetchTickets} />

      {expanded && (
        <>
          {/* Tab switch: open remediation tickets, already-fixed tickets, and
              the same open tickets grouped into Bedrock's severity buckets for
              batch remediation. */}
          <div className="jira-tabs">
            <button
              className={`jira-tab ${view === 'open' ? 'active' : ''}`}
              onClick={() => setView('open')}
            >
              🎫 Open Tickets{openCount ? ` (${openCount})` : ''}
            </button>
            <button
              className={`jira-tab ${view === 'fixed' ? 'active' : ''}`}
              onClick={() => setView('fixed')}
            >
              ✅ Fixed{fixedTickets.length ? ` (${fixedTickets.length})` : ''}
            </button>
            <button
              className={`jira-tab ${view === 'batch' ? 'active' : ''}`}
              onClick={() => setView('batch')}
            >
              {/* Every ticket lands in exactly one of the four buckets
                  (including the not-yet-assessed one), so the whole-list length
                  is the sum of the bucket counts. */}
              🧩 Batch Remediation{tickets.length ? ` (${tickets.length})` : ''}
            </button>
          </div>

          {view === 'open' && (
          <>
          <p className="section-description">
            Status <strong>New</strong> · summary starts with <strong>Remediate</strong> · reporter <strong>SVC_ZAFRAN_JIRA_SYNC</strong>
            {lastUpdated && (
              <span className="jira-updated"> · Updated {new Date(lastUpdated).toLocaleString()}</span>
            )}
          </p>
          {loading ? (
            <div className="jira-loading"><div className="spinner-small" /><span>Loading tickets...</span></div>
          ) : error && tickets.length === 0 ? (
            <div className="jira-error">
              <span>⚠️ {error}</span>
              <button onClick={fetchTickets}>Retry</button>
            </div>
          ) : (
            <>
              {/* Stat cards */}
              <div className="jira-stats-grid">
                {totalCount !== null && (
                  <div className="jira-stat-card total-count">
                    <div className="stat-value">{totalCount.toLocaleString()}</div>
                    <div className="stat-label">Total in JIRA</div>
                  </div>
                )}
                <div className="jira-stat-card">
                  <div className="stat-value">{tickets.length}</div>
                  <div className="stat-label">Pulled Today</div>
                </div>
                {Object.entries(priorityCounts).map(([p, count]) => (
                  <div
                    key={p}
                    className="jira-stat-card clickable"
                    onClick={() => setPriorityFilter(priorityFilter === p ? '' : p)}
                    style={{ borderColor: PRIORITY_STYLE[p]?.color }}
                  >
                    <div className="stat-value" style={{ color: PRIORITY_STYLE[p]?.color }}>
                      {PRIORITY_STYLE[p]?.icon || '•'} {count}
                    </div>
                    <div className="stat-label">{p}</div>
                  </div>
                ))}
              </div>

              {/* Filter row */}
              <div className="jira-filters">
                <input
                  type="text"
                  className="jira-search"
                  placeholder="Search by JIRA key or summary…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="jira-search-clear"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
                <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                  <option value="">All Priorities</option>
                  {Object.keys(PRIORITY_STYLE).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {epicOptions.length > 1 && (
                  <select
                    value={epicFilter}
                    onChange={(e) => setEpicFilter(e.target.value)}
                    title="Filter tickets by EPIC"
                  >
                    <option value="">All EPICs</option>
                    {epicOptions.map((ep) => (
                      <option key={ep} value={ep}>
                        {ep}{epicCounts[ep] != null ? ` (${epicCounts[ep]})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <button className="refresh-btn-small" onClick={fetchTickets}>🔄 Refresh</button>
              </div>

              {/* Table */}
              {visible.length === 0 ? (
                <div className="jira-empty">
                  <span className="empty-icon">{search ? '🔍' : '✅'}</span>
                  <p>
                    {search
                      ? `No tickets match “${searchQuery.trim()}”.`
                      : 'No tickets match the filter.'}
                  </p>
                </div>
              ) : (
                <div className="jira-table-container">
                  <table className="jira-table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Team</th>
                        <th>Summary</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Assignee</th>
                        <th>Updated</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((t) => (
                        <tr key={t.key} className="clickable-row" onClick={() => setSelected(t)}>
                          <td>
                            <a href={t.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="jira-key">
                              {t.key}
                            </a>
                          </td>
                          <td>{t.team ? <span className="jira-team">{t.team}</span> : '—'}</td>
                          <td className="summary-cell">{t.summary}</td>
                          <td>
                            <span style={{ color: PRIORITY_STYLE[t.priority]?.color }}>
                              {PRIORITY_STYLE[t.priority]?.icon || ''} {t.priority || '-'}
                            </span>
                          </td>
                          <td><span className="jira-status">{t.status || '-'}</span></td>
                          <td>{t.assignee || 'Unassigned'}</td>
                          <td>{t.updated ? new Date(t.updated).toLocaleDateString() : '-'}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {isMultiRepo(t) ? (
                              // Multi-repo finding: show each affected repo's own
                              // outcome (PR / no change / pending / failed), AND
                              // always offer a re-run so a finding that failed
                              // across its repos (e.g. a token/SSO error) can be
                              // retried straight from the Open tab. Without this
                              // the multi-repo branch rendered results only and
                              // left the user no way to re-trigger the agent.
                              <div className="jira-multirepo-action">
                                {renderRepoResults(t.repoResults)}
                                <button
                                  className="jira-fix-btn"
                                  disabled={remediating === t.key}
                                  onClick={() => requestFix(t)}
                                  title="Re-run the iCOP agent for all repositories affected by this finding"
                                >
                                  {multiRepoActionLabel(t.key)}
                                </button>
                              </div>
                            ) : hasPr(t.key) || isPrPending(t.key) ? (
                              // PR created (or Copilot preparing it): show the PR
                              // link / "PR pending" instead of the Fix button.
                              remediationLinkOrPending(remediationStatus[t.key].prUrl, remediationStatus[t.key].prNumber)
                            ) : isSkipped(t.key) ? (
                              // No code change was required: show the outcome, no button.
                              <span
                                className="jira-fix-skipped"
                                title={remediationStatus[t.key].message || 'No code change required'}
                              >
                                ℹ️ No change needed
                              </span>
                            ) : (
                              <>
                                <button
                                  className="jira-fix-btn"
                                  disabled={remediating === t.key}
                                  onClick={() => requestFix(t)}
                                  title="iCOP agent reads this ticket from JIRA, generates a fix, and opens a PR"
                                >
                                  {fixButtonLabel(t.key)}
                                </button>
                                {['failed', 'error'].includes(remediationStatus[t.key]?.status) && (
                                  <div
                                    className="jira-fix-error"
                                    title={remediationStatus[t.key].error || remediationStatus[t.key].message}
                                  >
                                    ⚠️ {remediationStatus[t.key].message || 'Remediation failed'}
                                    {' — '}click for details
                                  </div>
                                )}
                                {remediationStatus[t.key]?.status === 'pr_closed' && (
                                  <div
                                    className="jira-fix-error"
                                    title={remediationStatus[t.key].message || 'The remediation pull request was closed without merging'}
                                  >
                                    🚫 PR closed without merging{' — '}re-run to try again
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    page={openPage}
                    pageCount={openPageCount}
                    totalItems={openTotal}
                    pageSize={PAGE_SIZE}
                    onPageChange={setOpenPage}
                  />
                </div>
              )}
            </>
          )}
          </>
          )}

          {view === 'fixed' && (
          <>
            <p className="section-description">
              Tickets remediated by iCOP (PR created) · retained for 90 days · current JIRA status refreshed daily
            </p>
            {fixedLoading ? (
              <div className="jira-loading"><div className="spinner-small" /><span>Loading fixed tickets...</span></div>
            ) : fixedTickets.length === 0 ? (
              <div className="jira-empty"><span className="empty-icon">🗂️</span><p>No fixed tickets yet. Fixes you create appear here and stay for 90 days.</p></div>
            ) : (
              <>
              {/* Category filter + search row (key / summary / team / fixed-by) */}
              <div className="jira-filters">
                <select
                  value={fixedBucketFilter}
                  onChange={(e) => setFixedBucketFilter(e.target.value)}
                  title="Filter by the Bedrock category the ticket was remediated under"
                  aria-label="Filter fixed tickets by category"
                >
                  <option value="">All categories</option>
                  {FIXED_BUCKET_FILTERS.map((bucket) => {
                    const style = BUCKET_LABEL[bucket];
                    const count = fixedBucketCounts[bucket] || 0;
                    return (
                      <option key={bucket} value={bucket}>
                        {style ? `${style.icon} ${style.label}` : 'Not assessed'} ({count})
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  className="jira-search"
                  placeholder="Search by key, summary, team or fixed by…"
                  value={fixedSearchQuery}
                  onChange={(e) => setFixedSearchQuery(e.target.value)}
                />
                {fixedSearchQuery && (
                  <button
                    type="button"
                    className="jira-search-clear"
                    onClick={() => setFixedSearchQuery('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              {fixedShown.length === 0 ? (
                <div className="jira-empty">
                  <span className="empty-icon">🔍</span>
                  <p>
                    No fixed tickets match
                    {fixedSearchQuery.trim() ? ` “${fixedSearchQuery.trim()}”` : ''}
                    {fixedBucketFilter
                      ? `${fixedSearchQuery.trim() ? ' in' : ''} the ${
                        BUCKET_LABEL[fixedBucketFilter]
                          ? BUCKET_LABEL[fixedBucketFilter].label
                          : 'Not assessed'} category`
                      : ''}.
                  </p>
                </div>
              ) : (
              <div className="jira-table-container">
                <table className="jira-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Team</th>
                      <th>Category</th>
                      <th>Summary</th>
                      <th>JIRA Status</th>
                      <th>PR</th>
                      <th>Fixed By</th>
                      <th>Fixed On</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fixedShown.map((t) => (
                      <tr key={t.key}>
                        <td>
                          <a href={t.url} target="_blank" rel="noopener noreferrer" className="jira-key">{t.key}</a>
                        </td>
                        <td>{t.team ? <span className="jira-team">{t.team}</span> : '—'}</td>
                        <td>{bucketBadge(t)}</td>
                        <td className="summary-cell">{t.summary || '-'}</td>
                        <td>{fixedStatusBadge(t.jiraStatus)}</td>
                        <td>
                          {isMultiRepo(t) ? (
                            renderRepoResults(t.repoResults)
                          ) : t.prUrl ? (
                            remediationLinkOrPending(t.prUrl, t.prNumber)
                          ) : t.remediationStatus === 'skipped' ? (
                            <span className="jira-fix-skipped" title={t.remediationMessage || 'No code change required'}>
                              ℹ️ No change
                            </span>
                          ) : '-'}
                        </td>
                        <td>{t.fixedBy || '-'}</td>
                        <td>{t.fixedAt ? new Date(t.fixedAt).toLocaleDateString() : '-'}</td>
                        <td>
                          <button
                            className="jira-fix-btn"
                            disabled={remediating === t.key}
                            onClick={() => requestFix(t)}
                            title="Run the iCOP agent again for this ticket and open a fresh PR"
                          >
                            {rerunButtonLabel(t.key)}
                          </button>
                          {['failed', 'error'].includes(remediationStatus[t.key]?.status) && (
                            <div
                              className="jira-fix-error"
                              title={remediationStatus[t.key].error || remediationStatus[t.key].message}
                            >
                              ⚠️ {remediationStatus[t.key].message || 'Remediation failed'}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination
                  page={fixedPage}
                  pageCount={fixedPageCount}
                  totalItems={fixedTotal}
                  pageSize={PAGE_SIZE}
                  onPageChange={setFixedPage}
                />
                <div className="jira-load-more">
                  <button onClick={fetchFixed}>🔄 Refresh</button>
                </div>
              </div>
              )}
              </>
            )}
          </>
          )}

          {view === 'batch' && (
          <>
            <p className="section-description">
              The open tickets above, grouped by Bedrock's assessment of each ticket's
              severity and the scope of the code change required — not by JIRA priority.
              Remediate a whole bucket in one run — one pull request per repository. Nothing
              is merged automatically.
            </p>
            {loading ? (
              <div className="jira-loading"><div className="spinner-small" /><span>Loading tickets...</span></div>
            ) : error && tickets.length === 0 ? (
              <div className="jira-error">
                <span>⚠️ {error}</span>
                <button onClick={fetchTickets}>Retry</button>
              </div>
            ) : (
              /* No ticket data is passed down: the buckets, their counts and the
                 epic breakdown all come from the server, which is what preview
                 and trigger act on. */
              <BatchRemediationSection
                appId={appId}
                onRefresh={() => { fetchTickets(); fetchFixed(); }}
              />
            )}
          </>
          )}
        </>
      )}

      {/* Detail modal - rendered via a portal to document.body so it is not
          trapped/hidden by any parent's transform/overflow/stacking context. */}
      {selected && ReactDOM.createPortal(
        <div className="jira-modal-overlay" onClick={() => setSelected(null)}>
          <div className="jira-modal" onClick={(e) => e.stopPropagation()}>
            <div className="jira-modal-header">
              <h3>
                <a href={selected.url} target="_blank" rel="noopener noreferrer">{selected.key}</a>
                <span style={{ color: PRIORITY_STYLE[selected.priority]?.color, marginLeft: 8 }}>
                  {PRIORITY_STYLE[selected.priority]?.icon} {selected.priority}
                </span>
              </h3>
              <button className="modal-close-btn" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="jira-modal-body">
              <h4>{selected.summary}</h4>
              <div className="jira-detail-grid">
                <div className="detail-item"><label>Key</label><span>{selected.key}</span></div>
                <div className="detail-item"><label>Priority</label>
                  <span style={{ color: PRIORITY_STYLE[selected.priority]?.color }}>
                    {PRIORITY_STYLE[selected.priority]?.icon || ''} {selected.priority || '-'}
                  </span>
                </div>
                <div className="detail-item"><label>Status</label><span>{selected.status || '-'}</span></div>
                <div className="detail-item"><label>Type</label><span>{selected.issueType || '-'}</span></div>
                <div className="detail-item"><label>Team</label><span>{selected.team || '—'}</span></div>
                <div className="detail-item"><label>Assignee</label><span>{selected.assignee || 'Unassigned'}</span></div>
                <div className="detail-item"><label>Reporter</label><span>{selected.reporter || '-'}</span></div>
                <div className="detail-item"><label>Parent</label><span>{selected.parentKey || '-'}</span></div>
                <div className="detail-item"><label>Created</label><span>{selected.created ? new Date(selected.created).toLocaleString() : '-'}</span></div>
                <div className="detail-item"><label>Updated</label><span>{selected.updated ? new Date(selected.updated).toLocaleString() : '-'}</span></div>
                {selected.labels && selected.labels.length > 0 && (
                  <div className="detail-item full-width">
                    <label>Labels</label>
                    <span>{selected.labels.join(', ')}</span>
                  </div>
                )}
              </div>

              <div className="jira-description">
                <label>Description</label>
                {selected.description ? (
                  <pre className="jira-description-text">{selected.description}</pre>
                ) : (
                  <p className="jira-description-empty">No description on this ticket.</p>
                )}
              </div>
            </div>
            {remediationStatus[selected.key] && (
              <div className={`jira-remediation-status status-${remediationStatus[selected.key].status}`}>
                <span className="remediation-headline">{remediationStatus[selected.key].message}</span>
                {remediationStatus[selected.key].prUrl &&
                  remediationLinkOrPending(remediationStatus[selected.key].prUrl, remediationStatus[selected.key].prNumber)}
                {['failed', 'error'].includes(remediationStatus[selected.key].status) && (
                  <div className="remediation-error-box">
                    <p className="remediation-error-hint">
                      ⚠️ The pull request could not be created. Something isn't working —
                      please review the details below and retry, or contact the platform team.
                    </p>
                    {remediationStatus[selected.key].error && (
                      <pre className="remediation-error-detail">{remediationStatus[selected.key].error}</pre>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="jira-modal-footer">
              <a href={selected.url} target="_blank" rel="noopener noreferrer" className="modal-btn secondary">Open in JIRA</a>
              {hasPr(selected.key) ? (
                // PR already exists: surface the PR link instead of the Fix button.
                <a
                  className="modal-btn primary"
                  href={remediationStatus[selected.key].prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ✅ View PR{remediationStatus[selected.key].prNumber ? ` #${remediationStatus[selected.key].prNumber}` : ''}
                </a>
              ) : isPrPending(selected.key) ? (
                // Copilot is preparing the PR: show the pending state, no button.
                <span className="modal-btn primary disabled" title="Copilot is preparing the fix. The pull request link will appear here once it is opened.">
                  ⏳ PR pending
                </span>
              ) : isSkipped(selected.key) ? (
                <span className="modal-btn secondary" title={remediationStatus[selected.key].message || 'No code change required'}>
                  ℹ️ No change needed
                </span>
              ) : (
                <>
                  <button
                    className="modal-btn primary"
                    disabled={remediating === selected.key}
                    onClick={() => requestFix(selected)}
                    title="iCOP agent reads this ticket from JIRA, generates a fix, and opens a PR"
                  >
                    {fixButtonLabel(selected.key)}
                  </button>
                </>
              )}
              <button className="modal-btn secondary" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* In-app confirmation dialog (replaces the native browser confirm). */}
      {confirmTicket && ReactDOM.createPortal(
        <div className="jira-modal-overlay" onClick={() => setConfirmTicket(null)}>
          <div className="jira-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="jira-confirm-header">
              <span className="jira-confirm-icon">🔧</span>
              <h3>Create a fix with iCOP?</h3>
            </div>
            <div className="jira-confirm-body">
              <div className="jira-confirm-ticket">
                <span className="jira-key">{confirmTicket.key}</span>
                <span className="jira-confirm-summary">{confirmTicket.summary || '—'}</span>
              </div>
              <p className="jira-confirm-text">
                The iCOP agent will read this ticket from JIRA, generate a code fix using
                {' '}<strong>{PROVIDER_LABEL}</strong>, and open a pull request for review.
                If no code change is required, it posts the details to the ticket instead of
                creating a pull request.
              </p>
            </div>
            <div className="jira-confirm-footer">
              <button className="modal-btn secondary" onClick={() => setConfirmTicket(null)}>
                Cancel
              </button>
              <button
                className="modal-btn primary"
                onClick={() => {
                  const t = confirmTicket;
                  setConfirmTicket(null);
                  runFix(t);
                }}
              >
                Create fix
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
    </>
  );
};

export default JiraTicketsSection;
